import { User, Calendar, Clock, Star } from 'lucide-react';

interface CallInfoBannerProps {
  representative: string;
  customer_name: string;
  timestamp: string;
  duration: string;
  overall_average: number | string;
  isDarkMode: boolean;
  getScoreColor: (score: number | string) => string;
  children?: React.ReactNode;
}

export default function CallInfoBanner({
  representative,
  customer_name,
  timestamp,
  duration,
  overall_average,
  isDarkMode,
  getScoreColor,
  children
}: CallInfoBannerProps) {
  const formatDate = (timestamp: string): string => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className={`border-b ${
      isDarkMode ? 'bg-[#0a1414] border-[#1a2424]' : 'bg-white border-gray-200'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="flex-1">
            <h2 className={`text-3xl font-bold ${isDarkMode ? 'text-white' : 'text-[#053333]'} mb-6`}>
              Call Analysis
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Representative Card */}
              <div className={`rounded-lg p-4 ${
                isDarkMode ? 'bg-[#050a0a] border border-[#1a2424]' : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    isDarkMode ? 'bg-blue-900/30' : 'bg-blue-100'
                  }`}>
                    <User className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className={`text-xs font-medium uppercase tracking-wide mb-1 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Sales Representative
                    </div>
                    <div className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                      {representative}
                    </div>
                  </div>
                </div>
              </div>

              {/* Customer Card */}
              <div className={`rounded-lg p-4 ${
                isDarkMode ? 'bg-[#050a0a] border border-[#1a2424]' : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    isDarkMode ? 'bg-green-900/30' : 'bg-green-100'
                  }`}>
                    <User className={`w-5 h-5 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className={`text-xs font-medium uppercase tracking-wide mb-1 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Customer Name
                    </div>
                    <div className={`text-lg font-semibold ${isDarkMode ? 'text-green-400' : 'text-[#1a9f6b]'}`}>
                      {customer_name}
                    </div>
                  </div>
                </div>
              </div>

              {/* Date Card */}
              <div className={`rounded-lg p-4 ${
                isDarkMode ? 'bg-[#050a0a] border border-[#1a2424]' : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    isDarkMode ? 'bg-purple-900/30' : 'bg-purple-100'
                  }`}>
                    <Calendar className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className={`text-xs font-medium uppercase tracking-wide mb-1 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Call Date
                    </div>
                    <div className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                      {formatDate(timestamp)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Duration Card */}
              <div className={`rounded-lg p-4 ${
                isDarkMode ? 'bg-[#050a0a] border border-[#1a2424]' : 'bg-gray-50 border border-gray-200'
              }`}>
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${
                    isDarkMode ? 'bg-orange-900/30' : 'bg-orange-100'
                  }`}>
                    <Clock className={`w-5 h-5 ${isDarkMode ? 'text-orange-400' : 'text-orange-600'}`} />
                  </div>
                  <div className="flex-1">
                    <div className={`text-xs font-medium uppercase tracking-wide mb-1 ${
                      isDarkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      Call Duration
                    </div>
                    <div className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-[#053333]'}`}>
                      {duration}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Overall Score */}
          <div className={`rounded-xl p-6 text-center ${
            isDarkMode ? 'bg-[#050a0a] border border-[#1a2424]' : 'bg-gray-100'
          }`}>
            <div className={`text-4xl font-bold mb-1`} style={{ color: getScoreColor(overall_average) }}>
              {overall_average || "N/A"}/10
            </div>
            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#373737]'}`}>
              Overall Score
            </div>
            <div className="flex items-center justify-center gap-1 mt-2">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-4 h-4 ${
                    i < Math.round((typeof overall_average === 'number' ? overall_average : parseFloat(String(overall_average))) / 2)
                      ? 'text-yellow-400 fill-current'
                      : isDarkMode ? 'text-gray-600' : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            {children && (
              <div className="mt-4">
                {children}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
